import React, { useEffect, useState, useCallback } from "react";
import { BarChart3, Calendar, CreditCard, Plus, RefreshCw, TrendingUp, Landmark } from "lucide-react";
import { Layout } from "../../../layouts/DashboardLayout";
import { TabType, FormDataType, FilterParams } from "./types/profitTracking";
import { useProfitData } from "./hooks/useProfitData";
import { useTreasuryData } from "./hooks/useTreasuryData";
import { useProfitCalculations } from "./hooks/useProfitCalculations";
import { ProfitFilters } from "./components/ProfitFilters";
import { ProfitEntryModal } from "./components/ProfitEntryModal";
import { OverviewTab } from "./components/OverviewTab";
import { DailyTab } from "./components/DailyTab";
import { EntriesTab } from "./components/EntriesTab";
import { ChartsTab } from "./components/ChartsTab";
import TreasuryTab from "./components/TreasuryTab";
import TreasuryEntryModal from "./components/TreasuryEntryModal";
import ExpenseModal from "./components/ExpenseModal";
import Loader from "../../../components/ui/Loader";

const TABS: { key: TabType; label: string; icon: any }[] = [
  { key: "overview", label: "Vue d'ensemble", icon: TrendingUp },
  { key: "daily", label: "Gain journalier / Moyen de paiement", icon: Calendar },
  { key: "entries", label: "Toutes les entrées", icon: CreditCard },
  { key: "charts", label: "Diagrammes", icon: BarChart3 },
  { key: "treasury", label: "Trésorerie", icon: Landmark },
];

const INITIAL_FORM_DATA: FormDataType = {
  date: new Date().toISOString().split("T")[0],
  heure: "",
  direction: "CAD_TO_XAF",
  country: "Cameroun",
  payment_method: "MTN",
  capital_amount: 0,
  capital_currency: "CAD",
  buy_platform: "BINANCE",
  buy_rate: 0,
  buy_fees: 0,
  usdt_obtained: 0,
  sell_platform: "BINANCE",
  sell_rate: 0,
  sale_amount: 0,
  izi_rate: 0,
  izi_amount: 0,
  profit_percentage: 0,
  gross_profit: 0,
  fees: 0,
  net_profit: 0,
  profit_cad: 0,
  expense_amount: 0,
  expense_currency: "XAF",
  expense_description: "",
  final_profit: 0,
  final_profit_cad: 0,
};

const ProfitTrackingPage: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [expenseEntry, setExpenseEntry] = useState<any>(null);

  // Treasury state
  const [showTreasuryForm, setShowTreasuryForm] = useState(false);
  const [editingTreasuryEntry, setEditingTreasuryEntry] = useState<any>(null);
  const [treasuryPage, setTreasuryPage] = useState(0);

  const [filterCountry, setFilterCountry] = useState("");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("");
  const [filterDirection, setFilterDirection] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 30;

  // Custom hooks
  const {
    globalSummary,
    countrySummary,
    paymentMethodSummary,
    dailyProfits,
    entries,
    totalEntries,
    allEntries,
    loading,
    countries,
    loadOverview,
    loadEntries,
    loadDaily,
    loadChartsData,
    loadCountries,
    createEntry,
    updateEntry,
    deleteEntry,
    recalculateAll,
    downloadEntriesExcel,
    downloadSummaryByCountryExcel,
    downloadSummaryByPaymentMethodExcel,
    downloadDailySummaryExcel,
  } = useProfitData();

  // Treasury hook
  const {
    entries: treasuryEntries,
    totalEntries: totalTreasuryEntries,
    summary: treasurySummary,
    loading: treasuryLoading,
    loadEntries: loadTreasuryEntries,
    loadSummary: loadTreasurySummary,
    createEntry: createTreasuryEntry,
    updateEntry: updateTreasuryEntry,
    deleteEntry: deleteTreasuryEntry,
    downloadExcel: downloadTreasuryExcel,
  } = useTreasuryData();

  const { formData, updateFormData, resetFormData } = useProfitCalculations(INITIAL_FORM_DATA);

  // Filter helpers
  const getFilterParams = useCallback((): FilterParams => {
    const params: FilterParams = {};
    if (filterCountry) params.country = filterCountry;
    if (filterPaymentMethod) params.payment_method = filterPaymentMethod;
    if (filterDirection) params.direction = filterDirection;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    return params;
  }, [filterCountry, filterPaymentMethod, filterDirection, filterDateFrom, filterDateTo]);

  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case "country":
        setFilterCountry(value);
        break;
      case "paymentMethod":
        setFilterPaymentMethod(value);
        break;
      case "direction":
        setFilterDirection(value);
        break;
      case "dateFrom":
        setFilterDateFrom(value);
        break;
      case "dateTo":
        setFilterDateTo(value);
        break;
    }
    setPage(0);
  };

  const resetFilters = () => {
    setFilterCountry("");
    setFilterPaymentMethod("");
    setFilterDirection("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setPage(0);
  };

  // Load data based on active tab
  useEffect(() => {
    const params = getFilterParams();
    if (activeTab === "overview") loadOverview(params);
    else if (activeTab === "entries") loadEntries({ ...params, skip: page * pageSize, limit: pageSize });
    else if (activeTab === "daily") loadDaily(params);
    else if (activeTab === "charts") loadChartsData(params);
    else if (activeTab === "treasury") {
      const tParams: any = {};
      if (filterDateFrom) tParams.date_from = filterDateFrom;
      if (filterDateTo) tParams.date_to = filterDateTo;
      loadTreasuryEntries({ ...tParams, skip: treasuryPage * pageSize, limit: pageSize });
      loadTreasurySummary(tParams);
    }
  }, [
    activeTab,
    getFilterParams,
    page,
    treasuryPage,
    loadOverview,
    loadEntries,
    loadDaily,
    loadChartsData,
    loadTreasuryEntries,
    loadTreasurySummary,
    filterDateFrom,
    filterDateTo,
  ]);

  // Load countries on mount
  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const handleSaveEntry = async () => {
    try {
      if (editingEntry) {
        await updateEntry(editingEntry.id, formData);
        setEditingEntry(null);
      } else {
        await createEntry(formData);
      }
      setShowAddForm(false);
      resetFormData();

      // Reload current tab data
      const params = getFilterParams();
      if (activeTab === "overview") loadOverview(params);
      else if (activeTab === "entries") loadEntries({ ...params, skip: page * pageSize, limit: pageSize });
      else if (activeTab === "daily") loadDaily(params);

      loadCountries();
    } catch (err) {
      console.error("Error saving entry", err);
      alert("Erreur lors de l'enregistrement de l'entrée");
    }
  };

  const handleSaveTreasuryEntry = async (data: any) => {
    try {
      if (editingTreasuryEntry) {
        await updateTreasuryEntry(editingTreasuryEntry.id, data);
        setEditingTreasuryEntry(null);
      } else {
        await createTreasuryEntry(data);
      }
      setShowTreasuryForm(false);
      const tParams: any = {};
      if (filterDateFrom) tParams.date_from = filterDateFrom;
      if (filterDateTo) tParams.date_to = filterDateTo;
      loadTreasuryEntries({ ...tParams, skip: treasuryPage * pageSize, limit: pageSize });
      loadTreasurySummary(tParams);
    } catch (err) {
      console.error("Error saving treasury entry", err);
      alert("Erreur lors de l'enregistrement");
    }
  };

  const handleEditTreasuryEntry = (entry: any) => {
    setEditingTreasuryEntry(entry);
    setShowTreasuryForm(true);
  };

  const handleDeleteTreasuryEntry = async (id: string) => {
    if (!window.confirm("Supprimer cette entrée de trésorerie ?")) return;
    try {
      await deleteTreasuryEntry(id);
      const tParams: any = {};
      if (filterDateFrom) tParams.date_from = filterDateFrom;
      if (filterDateTo) tParams.date_to = filterDateTo;
      loadTreasuryEntries({ ...tParams, skip: treasuryPage * pageSize, limit: pageSize });
      loadTreasurySummary(tParams);
    } catch (err) {
      console.error("Error deleting treasury entry", err);
    }
  };

  const handleDownloadTreasury = async () => {
    try {
      const params: any = {};
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      await downloadTreasuryExcel(params);
    } catch (err) {
      console.error("Download error", err);
      alert("Erreur lors du téléchargement");
    }
  };

  const handleEditEntry = (entry: any) => {
    setEditingEntry(entry);
    updateFormData({
      date: entry.date,
      heure: entry.heure || "",
      direction: entry.direction,
      country: entry.country,
      payment_method: entry.payment_method,
      capital_amount: entry.capital_amount,
      capital_currency: entry.capital_currency,
      buy_platform: entry.buy_platform || "BINANCE",
      buy_rate: entry.buy_rate || 0,
      buy_fees: entry.buy_fees || 0,
      usdt_obtained: entry.usdt_obtained || 0,
      sell_platform: entry.sell_platform || "BINANCE",
      sell_rate: entry.sell_rate || 0,
      sale_amount: entry.sale_amount || 0,
      izi_rate: entry.izi_rate || 0,
      izi_amount: entry.izi_amount || 0,
      profit_percentage: entry.profit_percentage || 0,
      gross_profit: entry.gross_profit || 0,
      fees: entry.fees || 0,
      net_profit: entry.net_profit || 0,
      profit_cad: entry.profit_cad || 0,
      expense_amount: entry.expense_amount || 0,
      expense_currency: entry.expense_currency || "XAF",
      expense_description: entry.expense_description || "",
      final_profit: entry.final_profit || 0,
      final_profit_cad: entry.final_profit_cad || 0,
    });
    setShowAddForm(true);
  };

  const handleAddExpense = (entry: any) => {
    setExpenseEntry(entry);
  };

  const handleDeleteEntry = async (id: string) => {
    if (!window.confirm("Supprimer cette entrée ?")) return;
    try {
      await deleteEntry(id);
      const params = getFilterParams();
      loadEntries({ ...params, skip: page * pageSize, limit: pageSize });
    } catch (err) {
      console.error("Error deleting", err);
    }
  };

  const refreshAll = async () => {
    try {
      await recalculateAll();
    } catch (err) {
      console.error("Error recalculating profits", err);
    }
    const params = getFilterParams();
    if (activeTab === "overview") loadOverview(params);
    else if (activeTab === "entries") loadEntries({ ...params, skip: page * pageSize, limit: pageSize });
    else if (activeTab === "daily") loadDaily(params);
    else if (activeTab === "treasury") {
      const tParams: any = {};
      if (filterDateFrom) tParams.date_from = filterDateFrom;
      if (filterDateTo) tParams.date_to = filterDateTo;
      loadTreasuryEntries({ ...tParams, skip: treasuryPage * pageSize, limit: pageSize });
      loadTreasurySummary(tParams);
    } else if (activeTab === "charts") {
      loadChartsData(params);
    }
  };

  const handleDownload = async (type: "entries" | "country" | "payment" | "daily") => {
    try {
      const params = getFilterParams();
      switch (type) {
        case "entries":
          await downloadEntriesExcel(params);
          break;
        case "country":
          await downloadSummaryByCountryExcel(params);
          break;
        case "payment":
          await downloadSummaryByPaymentMethodExcel(params);
          break;
        case "daily":
          await downloadDailySummaryExcel(params);
          break;
      }
    } catch (err) {
      console.error("Download error", err);
      alert("Erreur lors du téléchargement");
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 min-h-screen bg-gray-50">
        {/* Header */}
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Suivi des Bénéfices</h1>
            <p className="text-sm text-gray-500">Bénéfices par pays et moyens de paiement</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (activeTab === "treasury") {
                  setShowTreasuryForm(true);
                } else {
                  setShowAddForm(true);
                }
              }}
              className="flex gap-2 items-center px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              <Plus size={16} /> Nouvelle entrée
            </button>
            <button onClick={refreshAll} className="p-2 text-gray-600 bg-white rounded-lg border hover:bg-gray-50">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <ProfitFilters
          countries={countries}
          filterCountry={filterCountry}
          filterPaymentMethod={filterPaymentMethod}
          filterDirection={filterDirection}
          filterDateFrom={filterDateFrom}
          filterDateTo={filterDateTo}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
        />

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-200 rounded-lg w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-white text-green-700 shadow-sm" : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <>
          {loading ? (
            <Loader />
          ) : (
            <>
              {activeTab === "overview" && (
                <OverviewTab
                  globalSummary={globalSummary}
                  countrySummary={countrySummary}
                  paymentMethodSummary={paymentMethodSummary}
                  onDownload={handleDownload}
                />
              )}

              {activeTab === "daily" && <DailyTab dailyProfits={dailyProfits} onDownload={handleDownload} />}

              {activeTab === "entries" && (
                <EntriesTab
                  entries={entries}
                  totalEntries={totalEntries}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onEdit={handleEditEntry}
                  onDelete={handleDeleteEntry}
                  onDownload={handleDownload}
                  onAddExpense={handleAddExpense}
                />
              )}

              {activeTab === "charts" && (
                <ChartsTab
                  allEntries={allEntries}
                  dailyProfits={dailyProfits}
                  countrySummary={countrySummary}
                  paymentMethodSummary={paymentMethodSummary}
                />
              )}

              {activeTab === "treasury" && (
                <TreasuryTab
                  entries={treasuryEntries}
                  totalEntries={totalTreasuryEntries}
                  summary={treasurySummary}
                  page={treasuryPage}
                  pageSize={pageSize}
                  loading={treasuryLoading}
                  onPageChange={setTreasuryPage}
                  onEdit={handleEditTreasuryEntry}
                  onDelete={handleDeleteTreasuryEntry}
                  onDownload={handleDownloadTreasury}
                />
              )}
            </>
          )}
        </>

        {/* Modal */}
        <ProfitEntryModal
          isOpen={showAddForm}
          editingEntry={editingEntry}
          formData={formData}
          onClose={() => {
            setShowAddForm(false);
            setEditingEntry(null);
            resetFormData();
          }}
          onChange={updateFormData}
          onSave={handleSaveEntry}
        />

        <TreasuryEntryModal
          isOpen={showTreasuryForm}
          editingEntry={editingTreasuryEntry}
          onClose={() => {
            setShowTreasuryForm(false);
            setEditingTreasuryEntry(null);
          }}
          onSave={handleSaveTreasuryEntry}
        />

        {expenseEntry && (
          <ExpenseModal
            profitTrackingId={expenseEntry.id}
            date={expenseEntry.date}
            onClose={() => setExpenseEntry(null)}
            onSaved={() => {
              const params = getFilterParams();
              loadEntries({ ...params, skip: page * pageSize, limit: pageSize });
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default ProfitTrackingPage;
